import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// ---------- Auth token plumbing ----------
const TOKEN_KEY = "a6_auth_token";

export function getStoredToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

export function setStoredToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore (e.g. private mode) */
  }
}

// Attach bearer token on every request when present.
api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On any 401 from the server, drop the token and let the app's auth
// context detect the missing token + bounce to the login screen.
// Subscribers (set by AuthContext) get notified so they can re-render.
const onUnauthorizedListeners = new Set();
export function onUnauthorized(cb) {
  onUnauthorizedListeners.add(cb);
  return () => onUnauthorizedListeners.delete(cb);
}
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      setStoredToken(null);
      onUnauthorizedListeners.forEach((cb) => {
        try { cb(); } catch { /* ignore */ }
      });
    }
    return Promise.reject(err);
  }
);

// ---------- Auth endpoints ----------
export async function fetchAuthConfig() {
  const { data } = await api.get("/auth/config");
  return data; // { auth_required: bool }
}

export async function login({ username, password }) {
  const { data } = await api.post("/auth/login", { username, password });
  return data; // { token, expires_at, username, auth_disabled? }
}

export async function fetchMe() {
  const { data } = await api.get("/auth/me");
  return data; // { username, auth_required }
}

// Companies
export async function listCompanies() {
  const { data } = await api.get("/companies");
  return data;
}

export async function createCompany({ name, logoFile }) {
  const form = new FormData();
  form.append("name", name);
  if (logoFile) form.append("logo", logoFile);
  const { data } = await api.post("/companies", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function updateCompany(id, { name, logoFile }) {
  const form = new FormData();
  if (name !== undefined && name !== null) form.append("name", name);
  if (logoFile) form.append("logo", logoFile);
  const { data } = await api.put(`/companies/${id}`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function deleteCompany(id) {
  const { data } = await api.delete(`/companies/${id}`);
  return data;
}

// Presets
export async function listPresets(companyId, search) {
  const params = {};
  if (search) params.search = search;
  const { data } = await api.get(`/companies/${companyId}/presets`, { params });
  return data;
}

export async function createPreset(companyId, payload) {
  const { data } = await api.post(`/companies/${companyId}/presets`, payload);
  return data;
}

export async function deletePreset(presetId) {
  const { data } = await api.delete(`/presets/${presetId}`);
  return data;
}

// PDF generation
export async function generatePdf(payload) {
  const res = await api.post("/generate", payload, { responseType: "blob" });
  return res.data; // Blob
}
